import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import { spaNavigate } from "../../utils/navigation";
import { isCloudReachable } from "../../hooks/useOnlineStatus";
import "./SidebarOrgHeader.prefix.css";

const block = cn("sidebar-org-header");
const itemBlock = cn("sidebar-org-header-item");

const Avatar = ({ title, size = 28 }) => (
  <span
    className={block.elem("avatar").toClassName()}
    style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
  >
    {(title?.[0] ?? "?").toUpperCase()}
  </span>
);

/**
 * Sits at the very top of the sidebar. Shows the currently-active
 * organization as a one-line card with its name + a dropdown caret.
 * Clicking expands a menu of the user's other orgs to switch into —
 * mirrors how Label Studio (and Linear / GitHub) surface the active
 * workspace context at the top of their left rails.
 *
 * Decoupled from the old collapsible ``Organization`` group in the
 * main menu; that entry is now a plain navigation button (same as
 * Platform) so the left rail has exactly one affordance per item.
 */
export const SidebarOrgHeader = ({ canManageOrg = true }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [orgs, setOrgs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // ``localActiveOrgId`` is a session-scoped override on top of
  // ``user?.active_organization``. The Capacitor offline org switch
  // updates the cached whoami in IDB but the jotai atom doesn't
  // reliably refetch from queryClient — so the in-memory user
  // object keeps the OLD active_organization even though the
  // actual app state (projects, notifications) has switched.
  // Without this override the sidebar dropdown lies and the user
  // can't see which org they're really in.
  //
  // Persisted to sessionStorage so a soft re-mount via
  // ``history.replaceState`` keeps the right org label; cleared
  // on full reload (full reload pulls fresh whoami → atom is
  // accurate again).
  const [localActiveOrgId, setLocalActiveOrgId] = useState(() => {
    try {
      const v = window.sessionStorage?.getItem("afri:active-org-override");
      return v ? Number(v) : null;
    } catch {
      return null;
    }
  });
  // Server truth wins: once whoami reports a DIFFERENT active org than
  // the session override (e.g. staff switched via Platform → View, or
  // another tab switched), drop the override — otherwise the header kept
  // naming the old org while projects/home already showed the new one.
  useEffect(() => {
    if (localActiveOrgId == null) return;
    const serverOrg = user?.active_organization;
    if (serverOrg != null && serverOrg !== localActiveOrgId) {
      setLocalActiveOrgId(null);
      try {
        window.sessionStorage?.removeItem("afri:active-org-override");
      } catch {
        /* ignore */
      }
    }
  }, [user?.active_organization, localActiveOrgId]);
  const activeOrgId = localActiveOrgId ?? user?.active_organization;
  const activeOrg = orgs.find((o) => o.id === activeOrgId) ?? null;
  const otherOrgs = orgs.filter((o) => o.id !== activeOrgId);
  // Display-only fallback when the user has no active org (e.g. they
  // were removed from their only one). Rather than the generic
  // "Organization" label that looks like it's waiting to load, name
  // the state explicitly so the sidebar matches the empty-state copy
  // on /projects.
  const hasActiveOrg = !!activeOrgId;
  const activeTitle = !hasActiveOrg
    ? "No organization"
    : (activeOrg?.title ?? user?.active_organization_meta?.title ?? "Organization");

  // Detect desktop runtime so we can filter local-only orgs out of
  // the switcher. On the cloud install every org is the source of
  // truth and ``synced_with_cloud`` is always false (no peer); we
  // shouldn't filter there.
  const isDesktopApp =
    typeof window !== "undefined" &&
    (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
    window.location.port === "17080";

  // Fetch once. Using ``[api]`` causes an infinite re-fetch loop
  // because ApiContext re-creates its value on every error cascade.
  useEffect(() => {
    api.callApi("organizations").then((res) => {
      if (!res || res.error) return;
      const list = Array.isArray(res) ? res : (res.results ?? []);
      // Show every org the user is a member of, including ones that
      // haven't synced to the cloud peer. The desktop render below
      // adds a small "Local" badge to un-synced rows so the user can
      // tell which orgs exist only on this device — but the org is
      // still selectable. The previous behaviour hid them entirely,
      // which broke air-gapped installs (no rows ever have
      // ``synced_with_cloud=true``) and orgs created while offline.
      setOrgs(list);
      setTotalCount(res.count ?? list.length);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the dropdown when the user taps outside of it.
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  const switchOrg = useCallback(
    async (orgId) => {
      setOpen(false);
      // On desktop the org id we hold is a CLOUD org id (mirrored
      // locally via SyncMeta). The local Django's ``/api/organizations/
      // switch`` only knows local ids and wouldn't propagate the change
      // to the cloud anyway. Route through the afri_sync proxy that
      // calls cloud first, then flips the local active_organization to
      // the SyncMeta-resolved local pk so the sidebar stays in sync.
      const isDesktopApp =
        typeof window !== "undefined" &&
        (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
        window.location.port === "17080";

      let res;
      if (isDesktopApp) {
        const csrfMatch = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
        const csrf = csrfMatch ? decodeURIComponent(csrfMatch[1]) : "";
        // ``organization_id`` is whatever /api/organizations/ returned
        // — on desktop that's the LOCAL Organization.id; the proxy
        // resolves the SyncMeta-mapped cloud_id and forwards.
        const r = await fetch("/api/afri/sync/switch_org/", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": csrf },
          body: JSON.stringify({ organization_id: orgId }),
        });
        const text = await r.text();
        try {
          res = text ? JSON.parse(text) : {};
        } catch {
          res = {};
        }
        if (!r.ok) res = { error: res?.error || `Switch failed (${r.status})` };
      } else {
        res = await api.callApi("switchOrganization", {
          body: { organization_id: orgId },
        });
      }
      if (!res || res.error) return;

      // Remember this pick for next boot. useEnsureActiveOrg reads
      // this key on login and auto-switches into it, so a returning
      // platform-owner with several orgs lands where they left off
      // instead of on the server's lowest-pk default.
      try {
        const uid = user?.id;
        if (uid != null && typeof window !== "undefined") {
          window.localStorage.setItem(`afri:last-active-org:${uid}`, String(orgId));
        }
      } catch {
        /* private browsing */
      }

      // Keep the operator on the same page when we can. Most pages
      // (/, /home, /organization/*, /settings/*, /platform/*, etc.)
      // are org-agnostic — they work identically under the new
      // active org. Project-scoped URLs (/projects/:id/...) embed a
      // project primary key that is org-local, so a project 42 in
      // the old org won't exist in the new one; for those we strip
      // back to the project list. /review-stream is similarly
      // project-scoped under a different prefix.
      const { pathname, search, hash } = window.location;
      const currentHref = `${pathname}${search}${hash}`;

      const isProjectScoped = /^\/projects\/\d+/.test(pathname) || /^\/projects\/new/.test(pathname);
      const isReviewStream = /^\/review-stream/.test(pathname);

      let target = currentHref;
      if (isProjectScoped || isReviewStream) {
        target = "/projects";
      }

      // Drop every cached query (and explicitly clear, not just
      // invalidate, so the stale data is *gone* — not held until a
      // refetch lands). Belt-and-suspenders against any cross-org
      // data leakage from components that observe the query
      // synchronously: even if they re-render before the refetch
      // resolves, there's nothing to read.
      queryClient.clear();

      // Capacitor: do a real WebView reload. The bundled local
      // server only resolves files in ``webDir`` so we can't
      // location.replace into an arbitrary route (``/projects``
      // 404s — webDir only has ``index.html``). Reloading at ``/``
      // serves index.html, the pre-boot script re-runs, the React
      // tree mounts fresh under the new ``active_organization``,
      // and every per-org useState in every component starts from
      // empty. This is the only way to GUARANTEE isolation: no
      // stale projectsList, no stale notifications, no stale
      // breadcrumbs, no stale Data Manager filters, etc. — there
      // is nothing left from the old org to leak. Web/desktop do
      // the same thing via ``location.replace(target)`` further
      // down (it triggers a real reload there too).
      //
      // OFFLINE on Capacitor: skip the full reload. The reload
      // was bouncing users to /auth/login — the pre-boot JWT
      // check + cached-whoami round trip apparently doesn't
      // converge cleanly when network probes are failing in the
      // background. Instead, dispatch the org-switched event so
      // the menubar/notifications hook re-keys, then trigger a
      // soft re-mount via queryClient. Less ironclad than a full
      // reload (some long-lived useState in deeply nested
      // components might keep stale values until the user
      // navigates) but every per-org REQUEST goes through React
      // Query — and that's been cleared. Acceptable trade-off.
      const isCapacitor = typeof window !== "undefined" && typeof window.Capacitor !== "undefined";
      if (isCapacitor) {
        try {
          window.dispatchEvent(
            new CustomEvent("afri:org-switched", {
              detail: { organization_id: orgId },
            }),
          );
        } catch {
          /* old WebView */
        }
        // Always full reload on Capacitor — same path online and
        // offline. Online: cloud-confirmed switch, post-reload
        // whoami fetch returns the new active_organization.
        // Offline: ``offline-fetch.ts``'s POST handler patched the
        // cached whoami before we got here, and the post-reload
        // whoami fetch hits ``replayPersisted`` which returns the
        // patched body. The previous offline soft-remount path
        // updated some pieces (projects filter via cached whoami
        // read) but not the jotai-tanstack-query atom that drives
        // the sidebar dropdown — full reload sidesteps all that
        // by re-mounting the React tree from scratch.
        try {
          window.sessionStorage?.removeItem("afri:active-org-override");
        } catch (_) {}
        queryClient.clear();
        window.location.replace("/");
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.location.replace(target);
        });
      });
    },
    [api, canManageOrg, queryClient],
  );

  const hasOthers = otherOrgs.length > 0;
  const extraBeyondShown = Math.max(0, totalCount - orgs.length);

  return (
    <div className={block.toClassName()} ref={rootRef}>
      <button
        type="button"
        className={block.elem("trigger").mod({ open }).toClassName()}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Active organization: ${activeTitle}. Click to switch.`}
        disabled={!hasOthers && totalCount <= 1}
        title={activeTitle}
      >
        <div className={block.elem("copy").toClassName()}>
          <span className={block.elem("tag").toClassName()}>Organization</span>
          <span className={block.elem("label").toClassName()}>{activeTitle}</span>
        </div>
        {(hasOthers || extraBeyondShown > 0) && (
          <span className={block.elem("caret").mod({ open }).toClassName()} aria-hidden>
            ▾
          </span>
        )}
      </button>

      {open && (
        <div className={block.elem("menu").toClassName()} role="menu">
          {otherOrgs.map((org) => {
            // ``Local`` badge — desktop-only — flags orgs whose data
            // lives only on this device (no cloud peer). Annotations
            // there are not backed up; the user can still switch
            // into the org to see / sync it later.
            const isLocalOnly = isDesktopApp && org.synced_with_cloud === false;
            return (
              <div key={org.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={itemBlock.toClassName()}
                  onClick={() => switchOrg(org.id)}
                  title={
                    isLocalOnly
                      ? `${org.title}. ${t("components.orgswitcher.sidebar_org_header.local_only_tooltip")}`
                      : org.title
                  }
                >
                  <Avatar title={org.title} size={18} />
                  <span className={itemBlock.elem("label").toClassName()}>{org.title}</span>
                  {isLocalOnly && (
                    <span className={itemBlock.elem("badge").mod({ local: true }).toClassName()}>
                      {t("components.orgswitcher.sidebar_org_header.local_badge")}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
          {extraBeyondShown > 0 && (
            <div role="none">
              <button
                type="button"
                role="menuitem"
                className={itemBlock.mod({ more: true }).toClassName()}
                onClick={() => {
                  setOpen(false);
                  window.location.href = "/organizations";
                }}
              >
                <span
                  className={block.elem("avatar").toClassName()}
                  style={{ width: 18, height: 18, fontSize: "var(--font-size-caption)" }}
                >
                  +{extraBeyondShown}
                </span>
                <span className={itemBlock.elem("label").toClassName()}>
                  {t("components.orgswitcher.sidebar_org_header.all_organizations")}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
